import { useEffect, useState } from 'react'
import { api } from '@carrent/api'
import { DAMAGE_TYPE_LABELS, DamageSchema, truckProjections, zoneLabel } from '@carrent/ui'

/**
 * Паспорт кузова машины.
 *
 * Схема показывает актуальное состояние, список ниже — историю:
 * появилось в поездке N, отремонтировано ремонтом M. Отремонтированные
 * записи не удаляются, потому что именно они отвечают на вопрос
 * «это повреждение уже чинили или его записали дважды».
 */

function fmt(value) {
  if (!value) return '—'
  return String(value).slice(0, 10)
}

export default function CarBodyTab({ car }) {
  const [damages, setDamages] = useState(null)
  const [projection, setProjection] = useState('front')
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(null)

  const projections = truckProjections(car.body_type,
    { hasTailLift: car.has_tail_lift })

  useEffect(() => {
    let alive = true
    api.get(`/cars/${car.id}/damages/history`)
      .then(({ data }) => { if (alive) setDamages(data.data?.damages || []) })
      .catch(() => { if (alive) setError('Не удалось загрузить историю кузова') })
    return () => { alive = false }
  }, [car.id])

  if (!projections) {
    return (
      <div className="alert alert--info">
        {car.body_type && car.body_type !== 'other'
          ? `Для кузова «${car.body_type_label || car.body_type}» схема пока не нарисована.`
          : 'Укажите тип кузова во вкладке «Редактирование», чтобы вести паспорт кузова.'}
      </div>
    )
  }

  const all = damages || []
  const active = all.filter((d) => d.status === 'active')
  // На схеме — только то, что относится к нынешнему кузову: после
  // смены типа старые точки некуда положить.
  const renderable = active.filter((d) => d.body_type_snapshot === car.body_type)
  const hidden = active.length - renderable.length

  return (
    <div className="car-body">
      {error && <div className="alert alert--warning">{error}</div>}

      <div className="car-body__tabs" role="tablist" aria-label="Проекции кузова">
        {projections.map((p) => (
          <button key={p.key} type="button" role="tab"
            aria-selected={p.key === projection}
            className={`car-body__tab${p.key === projection ? ' is-active' : ''}`}
            onClick={() => setProjection(p.key)}>
            {p.label}
          </button>
        ))}
      </div>

      <DamageSchema
        projections={projections}
        activeKey={projection}
        marks={renderable.map((d) => ({ ...d, status: 'existing' }))}
        loading={!damages}
        disabled
        onMarkTap={setOpen}
      />

      <p className="car-body__summary">
        Активных повреждений: {active.length}
        {hidden > 0 && (
          <span className="car-body__hidden">
            {' '}· {hidden} не показано: зафиксированы на другом типе кузова
          </span>
        )}
      </p>

      <h4 className="car-body__section">Таймлайн кузова</h4>
      {damages && all.length === 0 && (
        <p className="car-body__empty">Повреждений не зафиксировано.</p>
      )}
      <ul className="car-body__timeline">
        {all.map((d) => (
          <li key={d.id}
            className={`car-body__event car-body__event--${d.status}`}>
            <button type="button" className="car-body__event-btn" onClick={() => setOpen(d)}>
              <span className="car-body__event-zone">{zoneLabel(d.zone_id, {}, d.body_type_snapshot)}</span>
              <span className="car-body__event-type">
                {DAMAGE_TYPE_LABELS[d.damage_type] || d.damage_type}
              </span>
              <span className="car-body__event-dates">
                {fmt(d.created_at)}
                {d.status === 'repaired' && ` → отремонтировано ${fmt(d.resolved_at)}`}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {open && (
        <div className="car-body__drawer" role="dialog" aria-label="Повреждение">
          <div className="car-body__drawer-head">
            <p className="car-body__drawer-zone">
              {zoneLabel(open.zone_id, {}, open.body_type_snapshot)}
            </p>
            <button type="button" className="btn btn--ghost" onClick={() => setOpen(null)}>
              Закрыть
            </button>
          </div>
          {open.photo_url && (
            <img src={open.photo_url} alt="Фото повреждения" className="car-body__photo" />
          )}
          <dl className="car-body__meta">
            <div><dt>Тип</dt><dd>{DAMAGE_TYPE_LABELS[open.damage_type] || open.damage_type}</dd></div>
            <div><dt>Зафиксировано</dt><dd>{fmt(open.created_at)}</dd></div>
            <div><dt>Поездка-источник</dt><dd>{open.origin_trip_id ? `#${open.origin_trip_id}` : '—'}</dd></div>
            {open.status === 'repaired' && (
              <div><dt>Ремонт</dt><dd>#{open.repaired_repair_id} · {fmt(open.resolved_at)}</dd></div>
            )}
          </dl>
        </div>
      )}
    </div>
  )
}
